import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";
import Logo from "../components/Logo";

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [role, setRole] = useState(params.get("role") === "student" ? "student" : "coach");
  const [form, setForm] = useState({
    name: "", email: "", password: "",
    bio: "", goals: "", skill_level: 3.0, coach_id: "",
  });
  const [coaches, setCoaches] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (role === "student") {
      api.get("/coaches/public").then(({ data }) => setCoaches(data || [])).catch(() => {});
    }
  }, [role]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const payload = { ...form, role };
    if (role === "student") {
      if (!payload.coach_id) {
        setLoading(false);
        toast.error("Please select a coach");
        return;
      }
      payload.skill_level = parseFloat(payload.skill_level) || 3.0;
    } else {
      delete payload.coach_id;
      delete payload.skill_level;
      delete payload.goals;
    }
    const res = await register(payload);
    setLoading(false);
    if (res.ok) {
      toast.success("Account created");
      nav(role === "coach" ? "/coach" : "/student", { replace: true });
    } else {
      toast.error(res.error);
    }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-[#0A0A0A] text-white">
      <div className="hidden md:block relative overflow-hidden border-r border-white/10">
        <img
          src="https://images.pexels.com/photos/32975182/pexels-photo-32975182.jpeg?auto=compress&cs=tinysrgb&w=1200"
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-50"
          aria-hidden="true"
        />
        <div className="absolute inset-0 bg-gradient-to-tr from-black/50 via-black/70 to-black" />
        <div className="relative p-12 flex flex-col h-full">
          <Link to="/" className="text-xs uppercase tracking-[0.3em] text-[#38BDF8] font-bold flex items-center gap-2" data-testid="back-home-link">
            <ArrowLeft size={14} /> Back home
          </Link>
          <div className="mt-auto">
            <div className="font-display uppercase font-bold text-[11px] tracking-[0.3em] text-[#38BDF8]">Join PBCOACH.APP</div>
            <h2 className="font-display text-5xl font-black uppercase tracking-tighter leading-[0.9] mt-3">
              Claim your<br />
              coaching<br />
              <span className="text-[#38BDF8]">workspace.</span>
            </h2>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-8 md:p-16">
        <form onSubmit={onSubmit} className="w-full max-w-md" data-testid="register-form">
          <Logo className="h-32 w-auto mb-6" testId="register-logo" />
          <div className="text-[11px] tracking-[0.3em] uppercase text-[#38BDF8] font-bold">Create account</div>
          <h1 className="font-display text-5xl font-black uppercase tracking-tighter mt-3">Get started</h1>

          {/* Role switch */}
          <div className="mt-6 grid grid-cols-2 border border-white/10" data-testid="role-switch">
            <button
              type="button"
              onClick={() => setRole("coach")}
              className={`py-3 text-xs uppercase tracking-widest font-bold transition-colors ${
                role === "coach" ? "bg-[#38BDF8] text-black" : "text-white hover:bg-white/5"
              }`}
              data-testid="role-coach-button"
            >
              I'm a coach
            </button>
            <button
              type="button"
              onClick={() => setRole("student")}
              className={`py-3 text-xs uppercase tracking-widest font-bold transition-colors ${
                role === "student" ? "bg-[#38BDF8] text-black" : "text-white hover:bg-white/5"
              }`}
              data-testid="role-student-button"
            >
              I'm a student
            </button>
          </div>

          <div className="mt-8 space-y-5">
            <Field label="Full name" value={form.name} onChange={set("name")} required testId="register-name-input" />
            <Field label="Email" type="email" value={form.email} onChange={set("email")} required testId="register-email-input" />
            <Field label="Password" type="password" value={form.password} onChange={set("password")} required minLength={6} testId="register-password-input" />

            {role === "student" && (
              <>
                <div>
                  <label className="text-xs uppercase tracking-widest text-[#A3A3A3] font-bold">Your coach</label>
                  <select
                    value={form.coach_id}
                    onChange={set("coach_id")}
                    className="w-full bg-transparent border-0 border-b border-white/20 focus:border-[#38BDF8] outline-none py-3 text-lg"
                    data-testid="register-coach-select"
                    required
                  >
                    <option value="" className="bg-[#141414]">Select your coach…</option>
                    {coaches.map((c) => (
                      <option key={c.user_id} value={c.user_id} className="bg-[#141414]">{c.name}</option>
                    ))}
                  </select>
                </div>
                <Field label="Current skill (DUPR, approx)" type="number" step="0.1" min={1.0} max={6.0}
                       value={form.skill_level} onChange={set("skill_level")} testId="register-skill-input" />
                <Field label="Goals" value={form.goals} onChange={set("goals")} testId="register-goals-input"
                       placeholder="e.g. Improve consistency at the kitchen." />
              </>
            )}
          </div>

          <button
            type="submit"
            disabled={loading}
            className="mt-10 btn-primary w-full py-4 text-sm disabled:opacity-60"
            data-testid="register-submit"
          >
            {loading ? "Creating…" : "Create account"}
          </button>

          <div className="mt-6 text-sm text-[#A3A3A3]">
            Already have one? <Link to="/login" className="text-[#38BDF8] font-bold hover:underline" data-testid="login-link">Sign in</Link>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, type = "text", value, onChange, required, minLength, placeholder, testId, step, min, max }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-widest text-[#A3A3A3] font-bold">{label}</label>
      <input
        type={type}
        value={value}
        onChange={onChange}
        required={required}
        minLength={minLength}
        placeholder={placeholder}
        step={step}
        min={min}
        max={max}
        className="w-full bg-transparent border-0 border-b border-white/20 focus:border-[#38BDF8] outline-none py-3 text-lg"
        data-testid={testId}
      />
    </div>
  );
}
